import { useState } from "react";

import {
  DesktopTemplate,
  MobileTemplate,
  TemplatePreview,
} from "common/components/Templates";
import CreateOrEditTemplate from "common/components/Templates/CreateOrEditTemplate";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "common/components/ui/Dialog";
import { useCampaignContext } from "common/helpers/CampaignContext";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { CreatedTemplate, Template, TemplateType } from "common/types";

import useAudience from "../../../datahooks/useAudience";

interface NewTemplateProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  type: TemplateType;
  onCreated: (createdTemplate: CreatedTemplate) => void;
}

export default function NewTemplate({
  isOpen,
  setIsOpen,
  type,
  onCreated,
}: NewTemplateProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { owners } = useCampaignContext();
  const { audience } = useAudience();
  const { accounts } = useSelectedWorkspaceContext();

  const [previewTemplate, setPreviewTemplate] = useState<Template>();

  const senderProfiles = accounts.filter(({ id }) => owners.includes(id));
  const targetProfiles = audience?.slice(0, 3);

  const createOrEditTemplateComponent = (
    <CreateOrEditTemplate onSuccess={onCreated} initialData={type}>
      {(onSubmit, isLoading, form) => {
        const updatedTemplate = form.watch();
        const templatePreviewComponent = (
          <TemplatePreview
            previewTemplate={previewTemplate}
            updatedTemplate={updatedTemplate}
            onUpdatePreview={() => setPreviewTemplate(updatedTemplate)}
            senderProfiles={senderProfiles}
            targetProfiles={targetProfiles}
          />
        );
        const saveButton = (
          <Button
            variant="default-black"
            onClick={onSubmit}
            isLoading={isLoading}
          >
            Save
          </Button>
        );
        return isDesktop ? (
          <>
            <DesktopTemplate
              previewComponent={templatePreviewComponent}
              form={form}
              isCreatingNew
            />
            <div className="mt-8 flex justify-between">
              <DialogClose asChild>
                <Button variant="secondary-black" disabled={isLoading}>
                  Discard
                </Button>
              </DialogClose>
              {saveButton}
            </div>
          </>
        ) : (
          <MobileTemplate
            onClose={() => setIsOpen(false)}
            previewComponent={templatePreviewComponent}
            updatePreview={() => setPreviewTemplate(updatedTemplate)}
            saveButton={saveButton}
            form={form}
          />
        );
      }}
    </CreateOrEditTemplate>
  );

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="lg:w-[1024px]">
          <DialogTitle className="mb-8">Create template</DialogTitle>
          {createOrEditTemplateComponent}
        </DialogContent>
      </Dialog>
    );
  }
  if (isOpen) {
    return createOrEditTemplateComponent;
  }
}
